import React, { useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { apiCall } from "utils/apiClient";
import PropTypes from "prop-types";

const BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL;
const antIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />;

const PartnerVerification = ({ partner }) => {
    const partnerId = partner?.id;

    const [data, setData] = useState({
        partner: {},
        documents: [],
        bank_details: {},
    });
    const [loading, setLoading] = useState(true);

    const [bankRejectionReason, setBankRejectionReason] = useState("");
    const [showBankRejection, setShowBankRejection] = useState(false);

    const [docRejectionReason, setDocRejectionReason] = useState({});
    const [activeDocRejectId, setActiveDocRejectId] = useState(null);

    const normalizeStatus = (value) => {
        if (value === true || value === 1 || value === "1") return "verified";
        if (value === false || value === 0 || value === "0") return "pending";

        const status = String(value || "").toLowerCase().trim();
        if (!status) return "pending";

        if (["verified", "approve", "approved", "success", "completed", "done", "yes", "true"].includes(status)) {
            return "verified";
        }
        if (["rejected", "reject", "declined", "failed", "no"].includes(status)) {
            return "rejected";
        }
        return status;
    };

    const getStatusBadgeClass = (status) => {
        const normalized = normalizeStatus(status);
        if (normalized === "verified") return "bg-green-100 text-green-700";
        if (normalized === "rejected") return "bg-red-100 text-red-700";
        return "bg-yellow-100 text-yellow-700";
    };

    const formatStatusLabel = (status) => {
        const normalized = normalizeStatus(status);
        return normalized.charAt(0).toUpperCase() + normalized.slice(1).replace(/_/g, " ");
    };

    const calculateAge = (dob) => {
        if (!dob) return "-";
        const birth = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        return age;
    };

    const fetchPartner = async () => {
        if (!partnerId) return;

        try {
            const result = await apiCall({
                endpoint: `partner/onboarding-data/${partnerId}`,
                method: "GET",
            });
            setData(result.data);
        } catch {
            toast.error("Error fetching partner data");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyBank = async (status) => {
        if (status === "rejected" && !bankRejectionReason.trim()) {
            return toast.error("Rejection reason required");
        }

        try {
            const result = await apiCall({
                endpoint: "partner/verify-bank",
                method: "POST",
                data: {
                    partner_id: partnerId,
                    status,
                    rejection_reason: bankRejectionReason,
                    verified_by: localStorage.getItem("id"),
                },
            });

            if (result.status === 200) {
                toast.success("Bank verification updated");
                setShowBankRejection(false);
                setBankRejectionReason("");
                fetchPartner();
            }
        } catch {
            toast.error("Error verifying bank");
        }
    };

    const handleVerifyDocument = async (docId, status) => {
        if (status === "rejected" && !docRejectionReason[docId]?.trim()) {
            return toast.error("Rejection reason required");
        }

        try {
            const result = await apiCall({
                endpoint: `partner/verify-documents/${docId}`,
                method: "POST",
                data: {
                    verified_by: localStorage.getItem("id"),
                    status,
                    rejection_reason: docRejectionReason[docId],
                },
            });

            if (result.status === 200) {
                toast.success("Document verification updated");
                setActiveDocRejectId(null);
                fetchPartner();
            }
        } catch {
            toast.error("Error verifying document");
        }
    };

    useEffect(() => {
        fetchPartner();
    }, [partnerId]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <Spin indicator={antIcon} />
            </div>
        );
    }

    const apiPartner = data?.partner || {};
    const routePartner = partner || {};

    // Normalize old/new keys and prefer latest API data where available.
    const mergedPartner = {
        ...routePartner,
        ...apiPartner,
        name: apiPartner?.name || routePartner?.name || "",
        mobile: apiPartner?.mobile || routePartner?.mobile || "",
        dob: apiPartner?.dob || routePartner?.dob || "",
        age: apiPartner?.age || routePartner?.age || "",
        profession:
            apiPartner?.profession ||
            apiPartner?.work ||
            routePartner?.profession ||
            routePartner?.work ||
            "",
        team_size:
            apiPartner?.team_size ||
            apiPartner?.labour_count ||
            routePartner?.team_size ||
            routePartner?.labour_count ||
            "",
        service_areas:
            apiPartner?.service_areas ||
            apiPartner?.area ||
            routePartner?.service_areas ||
            routePartner?.area ||
            "",
        aadhaar_no:
            apiPartner?.aadhaar_no ||
            apiPartner?.aadhar_no ||
            routePartner?.aadhaar_no ||
            routePartner?.aadhar_no ||
            "",
        pan_no: apiPartner?.pan_no || routePartner?.pan_no || "",
    };
    const documents = Array.isArray(data?.documents) ? data.documents : [];
    const bankPayload = data?.bank_details || {};
    const partnerBankPayload = data?.partner || {};
    const bankDetails = {
        account_holder_name: partnerBankPayload?.account_holder_name || bankPayload.account_holder_name || "",
        bank_name: partnerBankPayload?.bank_name || bankPayload.bank_name || "",
        bank_branch: bankPayload.bank_branch || "",
        account_number: partnerBankPayload?.account_number || bankPayload.account_number || "",
        ifsc_code: partnerBankPayload?.ifsc_code || bankPayload.ifsc_code || "",
        upi_id: partnerBankPayload?.upi_id || bankPayload.upi_id || "",
        status: bankPayload.status || "",
        rejection_reason: bankPayload.rejection_reason || "",
    };
    const documentsStatus = normalizeStatus(
        mergedPartner.documents_verified ?? data?.partner?.documents_verified
    );
    const bankStatus = normalizeStatus(
        bankDetails.status ?? mergedPartner.bank_verified ?? data?.partner?.bank_verified
    );
    const renderStatusPill = (status) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusBadgeClass(status)}`}>
            {formatStatusLabel(status)}
        </span>
    );
    const personalInfoItems = [
        { label: "Name", value: mergedPartner.name },
        { label: "Mobile", value: mergedPartner.mobile },
        {
            label: "DOB",
            value: mergedPartner.dob
                ? `${mergedPartner.dob} (${calculateAge(mergedPartner.dob)} yrs)`
                : mergedPartner.age
                    ? `${mergedPartner.age} yrs`
                    : "-",
        },
        { label: "Work", value: mergedPartner.profession || mergedPartner.work },
        { label: "Labour Count", value: mergedPartner.team_size || mergedPartner.labour_count },
        { label: "Area", value: mergedPartner.service_areas || mergedPartner.area },
        { label: "Aadhaar No", value: mergedPartner.aadhaar_no },
        { label: "PAN No", value: mergedPartner.pan_no },
    ];
    const bankInfoItems = [
        { label: "Account Holder", value: bankDetails.account_holder_name },
        { label: "Bank Name", value: bankDetails.bank_name },
        { label: "Bank Branch", value: bankDetails.bank_branch },
        { label: "Account Number", value: bankDetails.account_number },
        { label: "IFSC", value: bankDetails.ifsc_code },
        { label: "UPI ID", value: bankDetails.upi_id },
    ];

    return (
        <>
            <Toaster position="top-center" reverseOrder={false} />

            <div className="space-y-3">
                <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-slate-50 p-3 shadow-sm">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Partner Verification</h2>
                            <p className="mt-0.5 text-xs text-gray-600">
                                Quick review for partner profile, bank details and document approvals.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1">
                                <span className="text-xs font-medium text-gray-600">Documents</span>
                                {renderStatusPill(documentsStatus)}
                            </div>
                            <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1">
                                <span className="text-xs font-medium text-gray-600">Bank</span>
                                {renderStatusPill(bankStatus)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-3 xl:grid-cols-5">
                    <div className="rounded-xl border bg-white p-3 shadow-sm xl:col-span-3">
                        <h3 className="mb-2 text-sm font-semibold text-gray-900">Personal Info</h3>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {personalInfoItems.map((item) => (
                                <div key={item.label} className="rounded-lg bg-gray-50 px-2.5 py-1.5">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-500">{item.label}</p>
                                    <p className="mt-0.5 break-words text-xs font-medium text-gray-900">{item.value || "-"}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-xl border bg-white p-3 shadow-sm xl:col-span-2">
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-gray-900">Bank Details</h3>
                            {renderStatusPill(bankStatus)}
                        </div>

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {bankInfoItems.map((item) => (
                                <div key={item.label} className="rounded-lg bg-gray-50 px-2.5 py-1.5">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-500">{item.label}</p>
                                    <p className="mt-0.5 break-words text-xs font-medium text-gray-900">{item.value || "-"}</p>
                                </div>
                            ))}
                        </div>

                        {bankDetails.rejection_reason && (
                            <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                                <strong>Reason:</strong> {bankDetails.rejection_reason}
                            </div>
                        )}

                        <div className="mt-2 grid grid-cols-2 gap-2">
                            <button
                                onClick={() => handleVerifyBank("verified")}
                                className="rounded-md bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                            >
                                Verify
                            </button>
                            <button
                                onClick={() => setShowBankRejection(true)}
                                className="rounded-md border border-red-400 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                            >
                                Reject
                            </button>
                        </div>

                        {showBankRejection && (
                            <div className="mt-2 rounded-lg border border-gray-200 p-2">
                                <textarea
                                    rows={2}
                                    className="w-full rounded border p-2 text-sm"
                                    placeholder="Enter bank rejection reason..."
                                    value={bankRejectionReason}
                                    onChange={(e) => setBankRejectionReason(e.target.value)}
                                />
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => handleVerifyBank("rejected")}
                                        className="rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                                    >
                                        Submit
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowBankRejection(false);
                                            setBankRejectionReason("");
                                        }}
                                        className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="rounded-xl border bg-white p-3 shadow-sm">
                    <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
                        <span className="text-xs text-gray-500">{documents.length} uploaded</span>
                    </div>

                    {documents.length === 0 && (
                        <div className="rounded-lg border border-dashed bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                            No documents uploaded yet.
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2 xl:grid-cols-3">
                        {documents.map((doc) => (
                            <div key={doc.id} className="rounded-lg border bg-gray-50 p-2">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                                    <div className="sm:w-24 sm:flex-shrink-0">
                                        {doc.file_path ? (
                                            <img
                                                src={`${BASE_URL}${doc.file_path}`}
                                                alt={doc.type || "document"}
                                                className="h-24 w-full rounded border bg-white object-contain"
                                            />
                                        ) : (
                                            <div className="flex h-24 w-full items-center justify-center rounded border bg-white px-2 text-[11px] text-gray-500">
                                                No preview
                                            </div>
                                        )}
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="mb-1.5 flex items-center justify-between gap-2">
                                            <p className="truncate text-xs font-semibold capitalize text-gray-900">
                                                {(doc.type || "document").replace("_", " ")}
                                            </p>
                                            {renderStatusPill(doc.status ?? doc.verification_status ?? doc.is_verified)}
                                        </div>

                                        {doc.rejection_reason && (
                                            <div className="mb-1.5 rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
                                                <strong>Reason:</strong> {doc.rejection_reason}
                                            </div>
                                        )}

                                        <div className="grid grid-cols-2 gap-1.5">
                                            <button
                                                onClick={() => handleVerifyDocument(doc.id, "verified")}
                                                className="rounded-md bg-green-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                                            >
                                                Verify
                                            </button>
                                            <button
                                                onClick={() => setActiveDocRejectId(doc.id)}
                                                className="rounded-md border border-red-400 px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                                            >
                                                Reject
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {activeDocRejectId === doc.id && (
                                    <div className="mt-2 rounded-lg border border-gray-200 p-2">
                                        <textarea
                                            rows={2}
                                            className="w-full rounded border p-2 text-xs"
                                            placeholder="Enter rejection reason..."
                                            value={docRejectionReason[doc.id] || ""}
                                            onChange={(e) =>
                                                setDocRejectionReason((prev) => ({
                                                    ...prev,
                                                    [doc.id]: e.target.value,
                                                }))
                                            }
                                        />
                                        <button
                                            onClick={() => handleVerifyDocument(doc.id, "rejected")}
                                            className="mt-1.5 w-full rounded-md bg-red-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                                        >
                                            Submit Rejection
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

PartnerVerification.propTypes = {
    partner: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        mobile: PropTypes.string,
        dob: PropTypes.string,
        profession: PropTypes.string,
        team_size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        service_areas: PropTypes.string,
        aadhaar_no: PropTypes.string,
        pan_no: PropTypes.string,
    }).isRequired,
};


export default PartnerVerification;
